import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import {
  Settings, Users, Package, FileText, Home, Upload, Search,
  CheckCircle, XCircle, Clock, RefreshCw, Send, Eye, Trash2,
  Edit, Plus, Save, X, Download, ChevronDown, ChevronUp,
  AlertCircle, FileSpreadsheet, Server, Database, Building2,
  MapPin, Store, Wrench, LogOut, Lock, User, Globe
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== AUTH CONTEXT ====================

const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('edi_token');
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('edi_token');
          setUser(false);
        })
        .finally(() => setLoading(false));
    } else {
      setUser(false);
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    localStorage.setItem('edi_token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('edi_token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// API helper with auth
const api = {
  _getHeaders: () => {
    const token = localStorage.getItem('edi_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
  get: async (endpoint) => {
    const res = await axios.get(`${API}${endpoint}`, { headers: api._getHeaders() });
    return res.data;
  },
  post: async (endpoint, data) => {
    const res = await axios.post(`${API}${endpoint}`, data, { headers: api._getHeaders() });
    return res.data;
  },
  put: async (endpoint, data) => {
    const res = await axios.put(`${API}${endpoint}`, data, { headers: api._getHeaders() });
    return res.data;
  },
  delete: async (endpoint, config = {}) => {
    const res = await axios.delete(`${API}${endpoint}`, { headers: api._getHeaders(), ...config });
    return res.data;
  },
  upload: async (endpoint, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API}${endpoint}`, formData, {
      headers: { ...api._getHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }
};

// Add axios interceptor for 401 responses
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && !error.config.url?.includes('/auth/')) {
      localStorage.removeItem('edi_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Date formatting helpers using configured timezone
const getConfiguredTimezone = () => localStorage.getItem('edi_timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDateTime = (isoString) => {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString('es-ES', {
      timeZone: getConfiguredTimezone(),
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return new Date(isoString).toLocaleString('es-ES'); }
};

const formatDate = (isoString) => {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleDateString('es-ES', {
      timeZone: getConfiguredTimezone()
    });
  } catch { return new Date(isoString).toLocaleDateString('es-ES'); }
};

// ==================== LOGIN PAGE ====================

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error de autenticación');
    }
    setLoading(false);
  };

  return (
    <div className="login-container" data-testid="login-page">
      <div className="login-card">
        <div className="login-header">
          <FileSpreadsheet size={36} />
          <h1>Odoo EDI Connect</h1>
          <p>Iniciar sesión</p>
        </div>
        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error" data-testid="login-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          <div className="form-group">
            <label>Usuario</label>
            <div className="input-icon-wrapper">
              <User size={18} />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Nombre de usuario"
                autoFocus
                required
                data-testid="login-username-input"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Contraseña</label>
            <div className="input-icon-wrapper">
              <Lock size={18} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                required
                data-testid="login-password-input"
              />
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-primary login-btn"
            disabled={loading || !username || !password}
            data-testid="login-submit-btn"
          >
            {loading ? <RefreshCw size={18} className="spin" /> : <LogOut size={18} />}
            Entrar
          </button>
        </form>
      </div>
    </div>
  );
};

// Sidebar Navigation
const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navItems = [
    { path: "/", icon: Home, label: "Dashboard" },
    { path: "/config", icon: Settings, label: "Configuración" },
    { path: "/utilities", icon: Wrench, label: "Utilidades" },
    { path: "/users", icon: Users, label: "Usuarios" },
    { path: "/clients", icon: Building2, label: "Clientes EDI" },
    { path: "/products", icon: Package, label: "Productos EAN" },
    { path: "/invoices", icon: FileText, label: "Facturas" },
  ];

  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-header">
        <FileSpreadsheet size={28} />
        <span>Odoo EDI Connect</span>
      </div>
      <nav className="sidebar-nav">
        {navItems.map(({ path, icon: Icon, label }) => (
          <Link
            key={path}
            to={path}
            className={`nav-item ${location.pathname === path ? 'active' : ''}`}
            data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <Icon size={20} />
            <span>{label}</span>
          </Link>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="user-info">
          <User size={16} />
          <span>{user?.username}</span>
        </div>
        <button className="btn-logout" onClick={logout} data-testid="logout-btn" title="Cerrar sesión">
          <LogOut size={18} />
        </button>
      </div>
    </aside>
  );
};

// Toast notifications
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`} data-testid="toast">
      {type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
      <span>{message}</span>
      <button onClick={onClose}><X size={16} /></button>
    </div>
  );
};

// Dashboard
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/stats')
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Cargando...</div>;

  return (
    <div className="page" data-testid="dashboard-page">
      <h1>Dashboard</h1>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon clients"><Building2 size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats?.companies?.edi_enabled || 0}</span>
            <span className="stat-label">Compañías EDI</span>
            <span className="stat-sub">de {stats?.companies?.total || 0} totales</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon points"><MapPin size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats?.delivery_points?.active || 0}</span>
            <span className="stat-label">Puntos de Entrega</span>
            <span className="stat-sub">activos</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon products"><Package size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats?.products?.total || 0}</span>
            <span className="stat-label">Códigos EAN</span>
            <span className="stat-sub">{stats?.products?.groups || 0} grupo(s)</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pending"><Clock size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats?.invoices?.pending || 0}</span>
            <span className="stat-label">Facturas Pendientes</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon sent"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats?.invoices?.sent || 0}</span>
            <span className="stat-label">Enviadas</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Configuration Page
const Configuration = () => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [toast, setToast] = useState(null);
  const [serverTime, setServerTime] = useState(null);

  const COMMON_TIMEZONES = [
    'Atlantic/Canary', 'Europe/Madrid', 'Europe/London', 'Europe/Paris',
    'Europe/Berlin', 'Europe/Rome', 'Europe/Lisbon', 'America/New_York',
    'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Sao_Paulo', 'America/Mexico_City', 'America/Bogota',
    'America/Argentina/Buenos_Aires', 'Asia/Tokyo', 'Asia/Shanghai',
    'Australia/Sydney', 'Pacific/Auckland', 'UTC'
  ];

  const fetchServerTime = useCallback(() => {
    api.get('/config/server-time').then(setServerTime).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/config').then(c => {
      setConfig(c);
      if (c.timezone) localStorage.setItem('edi_timezone', c.timezone);
    }).finally(() => setLoading(false));
    fetchServerTime();
    const interval = setInterval(fetchServerTime, 10000);
    return () => clearInterval(interval);
  }, [fetchServerTime]);

  const handleChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/config', config);
      if (config.timezone) localStorage.setItem('edi_timezone', config.timezone);
      setToast({ message: 'Configuración guardada', type: 'success' });
      fetchServerTime();
    } catch (e) {
      setToast({ message: 'Error al guardar', type: 'error' });
    }
    setSaving(false);
  };

  const testOdoo = async () => {
    setTestResults(prev => ({ ...prev, odoo: 'testing' }));
    try {
      const result = await api.post('/config/test-odoo');
      setTestResults(prev => ({ ...prev, odoo: result.success ? 'success' : 'error' }));
      setToast({ message: result.message, type: result.success ? 'success' : 'error' });
    } catch (e) {
      setTestResults(prev => ({ ...prev, odoo: 'error' }));
      setToast({ message: 'Error de conexión', type: 'error' });
    }
  };

  const testSftp = async () => {
    setTestResults(prev => ({ ...prev, sftp: 'testing' }));
    try {
      const result = await api.post('/config/test-sftp');
      setTestResults(prev => ({ ...prev, sftp: result.success ? 'success' : 'error' }));
      setToast({ message: result.message, type: result.success ? 'success' : 'error' });
    } catch (e) {
      setTestResults(prev => ({ ...prev, sftp: 'error' }));
      setToast({ message: 'Error de conexión', type: 'error' });
    }
  };

  const syncTimezone = () => {
    const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    handleChange('timezone', localTz);
    setToast({ message: `Zona horaria sincronizada: ${localTz}`, type: 'success' });
  };

  if (loading) return <div className="loading">Cargando...</div>;

  return (
    <div className="page" data-testid="config-page">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <h1>Configuración</h1>

      {/* Timezone Section */}
      <div className="config-section full-width" style={{ marginBottom: '1.5rem' }}>
        <div className="section-header">
          <Globe size={20} />
          <h2>Zona Horaria del Sistema</h2>
        </div>
        <div className="form-grid cols-3" style={{ alignItems: 'end' }}>
          <div className="form-group">
            <label>Zona Horaria</label>
            <select
              value={config.timezone || 'UTC'}
              onChange={(e) => handleChange('timezone', e.target.value)}
              data-testid="timezone-select"
              style={{ height: '38px' }}
            >
              {COMMON_TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Fecha y Hora del Servidor</label>
            <div data-testid="server-time-display" style={{
              padding: '8px 12px', background: 'var(--bg-secondary, #f3f4f6)',
              borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.95rem',
              border: '1px solid var(--border-color, #e5e7eb)', height: '38px',
              display: 'flex', alignItems: 'center', gap: '8px'
            }}>
              <Clock size={14} />
              {serverTime ? `${serverTime.formatted} (UTC${serverTime.utc_offset})` : 'Cargando...'}
            </div>
          </div>
          <div className="form-group">
            <button
              className="btn btn-secondary"
              onClick={syncTimezone}
              data-testid="sync-timezone-btn"
              style={{ height: '38px', width: '100%' }}
            >
              <RefreshCw size={16} />
              Sincronizar con mi equipo
            </button>
          </div>
        </div>
        {config.timezone && serverTime && config.timezone !== serverTime.timezone && (
          <div style={{
            marginTop: '0.5rem', padding: '8px 12px', background: '#fef3c7',
            borderRadius: '6px', fontSize: '0.85rem', color: '#92400e',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <AlertCircle size={14} />
            Zona horaria modificada. Pulsa "Guardar Configuración" para aplicar los cambios.
          </div>
        )}
      </div>
      
      <div className="config-grid">
        {/* Odoo Configuration */}
        <div className="config-section">
          <div className="section-header">
            <Database size={20} />
            <h2>Conexión Odoo</h2>
            <button 
              className={`btn-test ${testResults.odoo}`} 
              onClick={testOdoo}
              disabled={testResults.odoo === 'testing'}
              data-testid="test-odoo-btn"
            >
              {testResults.odoo === 'testing' ? <RefreshCw size={16} className="spin" /> : <Server size={16} />}
              Probar
            </button>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>URL de Odoo</label>
              <input
                type="text"
                value={config.odoo_url || ''}
                onChange={(e) => handleChange('odoo_url', e.target.value)}
                placeholder="https://tu-empresa.odoo.com"
                data-testid="odoo-url-input"
              />
            </div>
            <div className="form-group">
              <label>Base de Datos</label>
              <input
                type="text"
                value={config.odoo_db || ''}
                onChange={(e) => handleChange('odoo_db', e.target.value)}
                placeholder="nombre_base_datos"
                data-testid="odoo-db-input"
              />
            </div>
            <div className="form-group">
              <label>Usuario / Email</label>
              <input
                type="text"
                value={config.odoo_username || ''}
                onChange={(e) => handleChange('odoo_username', e.target.value)}
                placeholder="usuario@email.com"
                data-testid="odoo-username-input"
              />
            </div>
            <div className="form-group">
              <label>API Key / Contraseña</label>
              <input
                type="password"
                value={config.odoo_password || ''}
                onChange={(e) => handleChange('odoo_password', e.target.value)}
                placeholder="••••••••"
                data-testid="odoo-password-input"
              />
            </div>
          </div>
        </div>

        {/* SFTP Configuration */}
        <div className="config-section">
          <div className="section-header">
            <Upload size={20} />
            <h2>SFTP Seres</h2>
            <button 
              className={`btn-test ${testResults.sftp}`} 
              onClick={testSftp}
              disabled={testResults.sftp === 'testing'}
              data-testid="test-sftp-btn"
            >
              {testResults.sftp === 'testing' ? <RefreshCw size={16} className="spin" /> : <Server size={16} />}
              Probar
            </button>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Host SFTP</label>
              <input
                type="text"
                value={config.sftp_host || ''}
                onChange={(e) => handleChange('sftp_host', e.target.value)}
                placeholder="sftp.seres.es"
                data-testid="sftp-host-input"
              />
            </div>
            <div className="form-group">
              <label>Puerto</label>
              <input
                type="number"
                value={config.sftp_port || 22}
                onChange={(e) => handleChange('sftp_port', parseInt(e.target.value))}
                data-testid="sftp-port-input"
              />
            </div>
            <div className="form-group">
              <label>Usuario SFTP</label>
              <input
                type="text"
                value={config.sftp_username || ''}
                onChange={(e) => handleChange('sftp_username', e.target.value)}
                data-testid="sftp-username-input"
              />
            </div>
            <div className="form-group">
              <label>Contraseña SFTP</label>
              <input
                type="password"
                value={config.sftp_password || ''}
                onChange={(e) => handleChange('sftp_password', e.target.value)}
                placeholder="••••••••"
                data-testid="sftp-password-input"
              />
            </div>
            <div className="form-group full-width">
              <label>Carpeta Remota</label>
              <input
                type="text"
                value={config.sftp_path || '/'}
                onChange={(e) => handleChange('sftp_path', e.target.value)}
                placeholder="/facturas/entrantes"
                data-testid="sftp-path-input"
              />
            </div>
          </div>
        </div>

        {/* Sender Info */}
        <div className="config-section full-width">
          <div className="section-header">
            <FileText size={20} />
            <h2>Datos del Emisor (Tu Empresa)</h2>
          </div>
          <div className="form-grid cols-3">
            <div className="form-group">
              <label>Código EAN Emisor</label>
              <input
                type="text"
                value={config.sender_ean || ''}
                onChange={(e) => handleChange('sender_ean', e.target.value)}
                placeholder="8437014128000"
                data-testid="sender-ean-input"
              />
            </div>
            <div className="form-group">
              <label>Nombre/Razón Social</label>
              <input
                type="text"
                value={config.sender_name || ''}
                onChange={(e) => handleChange('sender_name', e.target.value)}
                data-testid="sender-name-input"
              />
            </div>
            <div className="form-group">
              <label>NIF/CIF</label>
              <input
                type="text"
                value={config.sender_vat || ''}
                onChange={(e) => handleChange('sender_vat', e.target.value)}
                placeholder="B12345678"
                data-testid="sender-vat-input"
              />
            </div>
            <div className="form-group">
              <label>Dirección</label>
              <input
                type="text"
                value={config.sender_street || ''}
                onChange={(e) => handleChange('sender_street', e.target.value)}
                data-testid="sender-street-input"
              />
            </div>
            <div className="form-group">
              <label>Ciudad</label>
              <input
                type="text"
                value={config.sender_city || ''}
                onChange={(e) => handleChange('sender_city', e.target.value)}
                data-testid="sender-city-input"
              />
            </div>
            <div className="form-group">
              <label>Código Postal</label>
              <input
                type="text"
                value={config.sender_zip || ''}
                onChange={(e) => handleChange('sender_zip', e.target.value)}
                data-testid="sender-zip-input"
              />
            </div>
            <div className="form-group full-width">
              <label>Registro Mercantil</label>
              <input
                type="text"
                value={config.sender_registry || ''}
                onChange={(e) => handleChange('sender_registry', e.target.value)}
                placeholder="Tomo 1234, Folio 56, Hoja PO-12345"
                data-testid="sender-registry-input"
              />
            </div>
            <div className="form-group">
              <label>Teléfono</label>
              <input
                type="text"
                value={config.sender_phone || ''}
                onChange={(e) => handleChange('sender_phone', e.target.value)}
                placeholder="928700545"
                data-testid="sender-phone-input"
              />
            </div>
            <div className="form-group">
              <label>Fax</label>
              <input
                type="text"
                value={config.sender_fax || ''}
                onChange={(e) => handleChange('sender_fax', e.target.value)}
                placeholder="928248007"
                data-testid="sender-fax-input"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="actions-bar">
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="save-config-btn">
          {saving ? <RefreshCw size={18} className="spin" /> : <Save size={18} />}
          Guardar Configuración
        </button>
      </div>
    </div>
  );
};

// Companies/Clients Page
const ClientsPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState(null);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [editingPoint, setEditingPoint] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);

  const loadCompanies = useCallback(() => {
    setLoading(true);
    api.get(`/companies${search ? `?search=${encodeURIComponent(search)}` : ''}`)
      .then(setCompanies)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const syncFromOdoo = async () => {
    setSyncing(true);
    try {
      const result = await api.post('/companies/sync-odoo');
      setToast({ message: result.message, type: 'success' });
      loadCompanies();
    } catch (e) {
      setToast({ message: e.response?.data?.detail || 'Error al sincronizar', type: 'error' });
    }
    setSyncing(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const result = await api.upload('/delivery-points/import-excel', file);
      setToast({ 
        message: `${result.message}. No encontrados: ${result.not_found?.length || 0}`, 
        type: 'success' 
      });
      loadCompanies();
    } catch (e) {
      setToast({ message: 'Error al importar Excel', type: 'error' });
    }
    setImporting(false);
    e.target.value = '';
  };

  const saveDeliveryPoint = async (pointData) => {
    try {
      await api.put(`/delivery-points/${pointData.id}`, pointData);
      setToast({ message: 'Punto de entrega actualizado', type: 'success' });
      setEditingPoint(null);
      loadCompanies();
    } catch (e) {
      setToast({ message: 'Error al guardar', type: 'error' });
    }
  };

  const deleteCompany = async (company) => {
    if (!window.confirm(`¿Eliminar "${company.name}" y todos sus puntos de entrega?`)) return;
    try {
      await api.delete(`/companies/${company.id}`);
      setToast({ message: 'Compañía eliminada', type: 'success' });
      loadCompanies();
    } catch (e) {
      setToast({ message: 'Error al eliminar', type: 'error' });
    }
  };

  const saveCompany = async () => {
    if (!editingCompany) return;
    try {
      await api.put(`/companies/${editingCompany.id}`, {
        use_parent_fiscal: editingCompany.use_parent_fiscal,
        company_registry: editingCompany.company_registry || null
      });
      setToast({ message: 'Compañía actualizada', type: 'success' });
      setEditingCompany(null);
      loadCompanies();
    } catch (e) {
      setToast({ message: 'Error al guardar', type: 'error' });
    }
  };

  return (
    <div className="page" data-testid="clients-page">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div className="page-header">
        <h1>Clientes EDI</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={syncFromOdoo} disabled={syncing} data-testid="sync-odoo-btn">
            {syncing ? <RefreshCw size={18} className="spin" /> : <RefreshCw size={18} />}
            Sincronizar Odoo
          </button>
          <label className="btn btn-secondary">
            {importing ? <RefreshCw size={18} className="spin" /> : <Upload size={18} />}
            Importar Excel P.O.
            <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} hidden />
          </label>
        </div>
      </div>

      <div className="info-box">
        <AlertCircle size={18} />
        <span>Solo se sincronizan clientes con la etiqueta <strong>EDI</strong> en Odoo. Los puntos operacionales se configuran por cada punto de entrega.</span>
      </div>

      <div className="search-bar">
        <Search size={18} />
        <input
          type="text"
          placeholder="Buscar por nombre o NIF..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="search-clients-input"
        />
      </div>

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : (
        <div className="companies-list" data-testid="companies-list">
          {companies.length === 0 ? (
            <div className="empty-state">
              <Building2 size={48} />
              <p>No hay compañías EDI. Añade la etiqueta "EDI" a los clientes en Odoo y sincroniza.</p>
            </div>
          ) : (
            companies.map(company => (
              <div key={company.id} className={`company-card ${company.is_edi_enabled ? 'edi-enabled' : ''}`}>
                <div className="company-header">
                  <div className="company-info" onClick={() => setExpandedCompany(expandedCompany === company.id ? null : company.id)}>
                    <Building2 size={20} className="company-icon" />
                    <div>
                      <div className="company-title-row">
                        <strong>{company.name}</strong>
                        {company.customer_groups?.map(g => (
                          <span key={g.id} className="cat-badge" data-testid={`company-group-${company.id}-${g.id}`}>{g.name}</span>
                        ))}
                      </div>
                      <span className="company-meta">{company.vat} · {company.delivery_points_count} punto(s) de entrega</span>
                    </div>
                  </div>
                  <div className="company-actions">
                    <button 
                      className="btn-icon danger" 
                      onClick={(e) => { e.stopPropagation(); deleteCompany(company); }}
                      title="Eliminar compañía"
                      data-testid={`delete-company-${company.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                    <span className="expand-icon" onClick={() => setExpandedCompany(expandedCompany === company.id ? null : company.id)}>
                      {expandedCompany === company.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </span>
                  </div>
                </div>
                
                {expandedCompany === company.id && (
                  <div className="company-details">
                    <div className="company-fiscal">
                      <div className="fiscal-header">
                        <h4>Datos Fiscales</h4>
                        <div className="fiscal-actions">
                          {editingCompany?.id === company.id ? (
                            <>
                              <button className="btn btn-primary btn-sm" onClick={saveCompany} data-testid={`save-company-${company.id}`}>
                                <Save size={14} /> Guardar
                              </button>
                              <button className="btn btn-secondary btn-sm" onClick={() => setEditingCompany(null)}>
                                <X size={14} /> Cancelar
                              </button>
                            </>
                          ) : (
                            <button className="btn-icon" onClick={() => setEditingCompany({...company})} title="Editar empresa" data-testid={`edit-company-${company.id}`}>
                              <Edit size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                      <p>{company.street}, {company.zip_code} {company.city}</p>
                      <p className="company-meta">
                        CIP/Código Proveedor: {editingCompany?.id === company.id ? (
                          <input
                            type="text"
                            value={editingCompany.company_registry || ''}
                            onChange={(e) => setEditingCompany({...editingCompany, company_registry: e.target.value})}
                            placeholder="Ej: 53213"
                            data-testid={`cip-input-${company.id}`}
                            style={{ display: 'inline-block', width: '150px', marginLeft: '4px', padding: '2px 6px', fontSize: 'inherit', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'inherit' }}
                          />
                        ) : (
                          <strong data-testid={`cip-${company.id}`}>{company.company_registry || '—'}</strong>
                        )}
                      </p>
                      {editingCompany?.id === company.id && (
                        <label className="toggle-fiscal" data-testid={`toggle-fiscal-${company.id}`}>
                          <input
                            type="checkbox"
                            checked={editingCompany.use_parent_fiscal !== false}
                            onChange={() => setEditingCompany({...editingCompany, use_parent_fiscal: !editingCompany.use_parent_fiscal})}
                          />
                          <span className="toggle-slider"></span>
                          <span className="toggle-label">Informacion Fiscal en EDI</span>
                        </label>
                      )}
                      {editingCompany?.id !== company.id && (
                        <label className="toggle-fiscal disabled">
                          <input
                            type="checkbox"
                            checked={company.use_parent_fiscal !== false}
                            disabled
                          />
                          <span className="toggle-slider"></span>
                          <span className="toggle-label">Informacion Fiscal en EDI</span>
                        </label>
                      )}
                    </div>
                    
                    <div className="delivery-points-section">
                      <h4><MapPin size={16} /> Puntos de Entrega</h4>
                      {company.delivery_points.length === 0 ? (
                        <p className="no-points">Sin puntos de entrega configurados</p>
                      ) : (
                        <div className="delivery-points-list">
                          {company.delivery_points.map(point => (
                            <div key={point.id} className="delivery-point">
                              {editingPoint?.id === point.id ? (
                                <DeliveryPointEditor 
                                  point={editingPoint} 
                                  onSave={saveDeliveryPoint}
                                  onCancel={() => setEditingPoint(null)}
                                />
                              ) : (
                                <>
                                  <div className="point-header">
                                    <Store size={16} />
                                    <strong>{point.name}</strong>
                                    <span className="point-address">{point.street}, {point.city}</span>
                                    <button className="btn-icon" onClick={() => setEditingPoint({...point})}>
                                      <Edit size={14} />
                                    </button>
                                  </div>
                                  <div className="point-codes">
                                    <div className="code-item">
                                      <label>BY</label>
                                      <span>{point.po_by || '-'}</span>
                                    </div>
                                    <div className="code-item">
                                      <label>DP</label>
                                      <span>{point.po_dp || '-'}</span>
                                    </div>
                                    <div className="code-item">
                                      <label>IV</label>
                                      <span>{point.po_iv || '-'}</span>
                                    </div>
                                    <div className="code-item">
                                      <label>PR</label>
                                      <span>{point.po_pr || '-'}</span>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Delivery Point Inline Editor
const DeliveryPointEditor = ({ point, onSave, onCancel }) => {
  const [data, setData] = useState(point);

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="point-editor">
      <div className="point-header">
        <Store size={16} />
        <strong>{point.name}</strong>
      </div>
      <div className="point-codes editing">
        <div className="code-item">
          <label>BY (Quien Pide)</label>
          <input
            type="text"
            value={data.po_by || ''}
            onChange={(e) => handleChange('po_by', e.target.value)}
            placeholder="8424818..."
          />
        </div>
        <div className="code-item">
          <label>DP (Entrega)</label>
          <input
            type="text"
            value={data.po_dp || ''}
            onChange={(e) => handleChange('po_dp', e.target.value)}
            placeholder="8424818..."
          />
        </div>
        <div className="code-item">
          <label>IV (Factura)</label>
          <input
            type="text"
            value={data.po_iv || ''}
            onChange={(e) => handleChange('po_iv', e.target.value)}
            placeholder="8424818..."
          />
        </div>
        <div className="code-item">
          <label>PR (Paga)</label>
          <input
            type="text"
            value={data.po_pr || ''}
            onChange={(e) => handleChange('po_pr', e.target.value)}
            placeholder="8424818..."
          />
        </div>
      </div>
      <div className="point-actions">
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancelar</button>
        <button className="btn btn-primary btn-sm" onClick={() => onSave(data)}>
          <Save size={14} /> Guardar
        </button>
      </div>
    </div>
  );
};

// Products Page - Organized by Customer Groups (compact)
const ProductsPage = () => {
  const [groups, setGroups] = useState([]);
  const [odooProducts, setOdooProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingEan, setEditingEan] = useState(null);
  const [addingProduct, setAddingProduct] = useState(null); // group_id
  const [showOdooImportModal, setShowOdooImportModal] = useState(null); // group_id
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [grps, prods, cats] = await Promise.all([
        api.get('/ean-groups'),
        api.get('/products/odoo-list'),
        api.get('/products/odoo-categories').catch(() => [])
      ]);
      setGroups(grps);
      setOdooProducts(prods);
      setCategories(cats);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const syncProducts = async () => {
    setSyncing(true);
    try {
      const result = await api.post('/products/sync-odoo');
      setToast({ message: result.message, type: 'success' });
      loadData();
    } catch (e) {
      setToast({ message: e.response?.data?.detail || 'Error al sincronizar', type: 'error' });
    }
    setSyncing(false);
  };

  const saveGroup = async (groupData) => {
    try {
      const payload = {
        name: groupData.name,
        category_ids: JSON.stringify(groupData.category_ids || []),
        category_names: JSON.stringify(groupData.category_names || [])
      };
      if (groupData.id) {
        await api.put(`/ean-groups/${groupData.id}`, payload);
      } else {
        await api.post('/ean-groups', payload);
      }
      setToast({ message: 'Grupo guardado', type: 'success' });
      setShowGroupModal(false);
      setEditingGroup(null);
      loadData();
    } catch (e) {
      setToast({ message: 'Error al guardar grupo', type: 'error' });
    }
  };

  const deleteGroup = async (group) => {
    if (!window.confirm(`¿Eliminar el grupo "${group.name}" y todos sus códigos EAN?`)) return;
    try {
      await api.delete(`/ean-groups/${group.id}`);
      setToast({ message: 'Grupo eliminado', type: 'success' });
      loadData();
    } catch (e) {
      setToast({ message: 'Error al eliminar', type: 'error' });
    }
  };

  const saveProductEan = async (groupId, eanData) => {
    try {
      if (eanData.id) {
        await api.put(`/ean-groups/${groupId}/products/${eanData.id}`, { ean_code: eanData.ean_code });
      } else {
        await api.post(`/ean-groups/${groupId}/products`, {
          group_id: groupId,
          odoo_product_id: eanData.odoo_product_id || 0,
          product_name: eanData.product_name,
          product_code: eanData.product_code || null,
          ean_code: eanData.ean_code
        });
      }
      setToast({ message: 'EAN guardado', type: 'success' });
      setEditingEan(null);
      setAddingProduct(null);
      loadData();
    } catch (e) {
      setToast({ message: 'Error al guardar', type: 'error' });
    }
  };

  const deleteProductEan = async (groupId, eanId) => {
    if (!window.confirm('¿Eliminar este código EAN?')) return;
    try {
      await api.delete(`/ean-groups/${groupId}/products/${eanId}`);
      setToast({ message: 'EAN eliminado', type: 'success' });
      loadData();
    } catch (e) {
      setToast({ message: 'Error al eliminar', type: 'error' });
    }
  };

  const handleImportExcel = async (groupId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const result = await api.upload(`/ean-groups/${groupId}/import-excel`, file);
      setToast({ message: result.message, type: 'success' });
      loadData();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Error al importar', type: 'error' });
    }
    e.target.value = '';
  };

  const filteredGroups = groups.map(g => {
    if (!search) return g;
    const filtered = g.products.filter(p =>
      p.product_name.toLowerCase().includes(search.toLowerCase()) ||
      p.ean_code.toLowerCase().includes(search.toLowerCase()) ||
      (p.product_code && p.product_code.toLowerCase().includes(search.toLowerCase()))
    );
    if (filtered.length > 0 || g.name.toLowerCase().includes(search.toLowerCase())) {
      return { ...g, products: search ? filtered : g.products };
    }
    return null;
  }).filter(Boolean);

  return (
    <div className="page" data-testid="products-page">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div className="page-header">
        <h1>Códigos EAN por Grupo</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={syncProducts} disabled={syncing} data-testid="sync-products-btn">
            {syncing ? <RefreshCw size={18} className="spin" /> : <RefreshCw size={18} />}
            Sync Productos
          </button>
          <button className="btn btn-primary" onClick={() => { setEditingGroup(null); setShowGroupModal(true); }} data-testid="new-group-btn">
            <Plus size={18} /> Nuevo Grupo
          </button>
        </div>
      </div>

      <div className="search-bar">
        <Search size={18} />
        <input
          type="text"
          placeholder="Buscar por producto, código o EAN..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="search-ean-input"
        />
      </div>

      {loading ? <div className="loading">Cargando...</div> : (
        <div className="groups-list" data-testid="ean-groups-list">
          {filteredGroups.length === 0 ? (
            <div className="empty-state">
              <Package size={48} />
              <p>No hay grupos. Crea un grupo de clientes para empezar a asignar códigos EAN.</p>
            </div>
          ) : (
            filteredGroups.map(group => {
              const isExpanded = expandedGroup === group.id;
              return (
                <div key={group.id} className="group-card" data-testid={`group-card-${group.id}`}>
                  <div className="group-header">
                    <div className="group-info" onClick={() => setExpandedGroup(isExpanded ? null : group.id)}>
                      <strong>{group.name}</strong>
                      <div className="group-badges">
                        {group.category_names.map((name, i) => (
                          <span key={i} className="cat-badge">{name}</span>
                        ))}
                      </div>
                      <span className="group-count">{group.product_count} prod.</span>
                    </div>
                    <div className="group-actions">
                      <label className="btn-icon" title="Importar Excel">
                        <FileSpreadsheet size={16} />
                        <input type="file" accept=".xlsx,.xls" onChange={(e) => handleImportExcel(group.id, e)} hidden />
                      </label>
                      <button className="btn-icon" onClick={() => { setEditingGroup(group); setShowGroupModal(true); }} title="Editar grupo" data-testid={`edit-group-${group.id}`}>
                        <Edit size={16} />
                      </button>
                      <button className="btn-icon danger" onClick={() => deleteGroup(group)} title="Eliminar grupo" data-testid={`delete-group-${group.id}`}>
                        <Trash2 size={16} />
                      </button>
                      <button className="btn-icon" onClick={() => setExpandedGroup(isExpanded ? null : group.id)} title="Expandir" data-testid={`expand-group-${group.id}`}>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="group-products">
                      {group.products.length === 0 && addingProduct !== group.id ? (
                        <p className="no-eans">Sin productos en este grupo</p>
                      ) : (
                        <table className="compact-table">
                          <thead>
                            <tr>
                              <th>Producto</th>
                              <th>Código</th>
                              <th>EAN/EDI</th>
                              <th style={{width: '70px'}}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.products.map(p => (
                              <tr key={p.id}>
                                {editingEan?.id === p.id ? (
                                  <>
                                    <td>{p.product_name}</td>
                                    <td>{p.product_code || '-'}</td>
                                    <td>
                                      <input
                                        type="text"
                                        className="inline-input"
                                        value={editingEan.ean_code}
                                        onChange={(e) => setEditingEan({...editingEan, ean_code: e.target.value})}
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') saveProductEan(group.id, editingEan);
                                          if (e.key === 'Escape') setEditingEan(null);
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <button className="btn-icon sm" onClick={() => saveProductEan(group.id, editingEan)}><Save size={14} /></button>
                                      <button className="btn-icon sm" onClick={() => setEditingEan(null)}><X size={14} /></button>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td>{p.product_name}</td>
                                    <td><code>{p.product_code || '-'}</code></td>
                                    <td><code className="ean-highlight">{p.ean_code}</code></td>
                                    <td>
                                      <button className="btn-icon sm" onClick={() => setEditingEan({...p})} title="Editar"><Edit size={14} /></button>
                                      <button className="btn-icon sm danger" onClick={() => deleteProductEan(group.id, p.id)} title="Eliminar"><Trash2 size={14} /></button>
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                            {addingProduct === group.id && (
                              <AddProductRow
                                odooProducts={odooProducts}
                                onSave={(data) => saveProductEan(group.id, data)}
                                onCancel={() => setAddingProduct(null)}
                              />
                            )}
                          </tbody>
                        </table>
                      )}
                      {addingProduct !== group.id && (
                        <div className="group-bottom-actions">
                          <button className="btn btn-outline-add" onClick={() => setAddingProduct(group.id)} data-testid={`add-product-to-group-${group.id}`}>
                            <Plus size={16} /> Añadir producto manual
                          </button>
                          <button className="btn btn-outline-import" onClick={() => setShowOdooImportModal(group.id)} data-testid={`import-odoo-to-group-${group.id}`}>
                            <Download size={16} /> Importar de Odoo
                          </button>
                          <label className="btn btn-outline-excel" data-testid={`import-excel-to-group-${group.id}`}>
                            <FileSpreadsheet size={16} /> Importar Excel
                            <input type="file" accept=".xlsx,.xls" onChange={(e) => handleImportExcel(group.id, e)} hidden />
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {showGroupModal && (
        <GroupModal
          group={editingGroup}
          categories={categories}
          onSave={saveGroup}
          onClose={() => { setShowGroupModal(false); setEditingGroup(null); }}
        />
      )}

      {showOdooImportModal && (
        <OdooProductPickerModal
          groupId={showOdooImportModal}
          odooProducts={odooProducts}
          existingProducts={groups.find(g => g.id === showOdooImportModal)?.products || []}
          onAdd={(groupId, products) => {
            const addAll = async () => {
              let count = 0;
              for (const p of products) {
                try {
                  await api.post(`/ean-groups/${groupId}/products`, {
                    group_id: groupId,
                    odoo_product_id: p.odoo_product_id,
                    product_name: p.product_name,
                    product_code: p.product_code || null,
                    ean_code: p.ean_code || ''
                  });
                  count++;
                } catch (e) { console.error(e); }
              }
              setToast({ message: `${count} producto(s) añadido(s) al grupo`, type: 'success' });
              setShowOdooImportModal(null);
              loadData();
            };
            addAll();
          }}
          onClose={() => setShowOdooImportModal(null)}
        />
      )}
    </div>
  );
};

// Compact row for adding a new product to a group
const AddProductRow = ({ odooProducts, onSave, onCancel }) => {
  const [productName, setProductName] = useState('');
  const [productCode, setProductCode] = useState('');
  const [eanCode, setEanCode] = useState('');
  const [odooId, setOdooId] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredOdoo = odooProducts.filter(p =>
    productName.length > 1 &&
    (p.name.toLowerCase().includes(productName.toLowerCase()) ||
     (p.default_code && p.default_code.toLowerCase().includes(productName.toLowerCase())))
  ).slice(0, 8);

  const selectProduct = (p) => {
    setProductName(p.name);
    setProductCode(p.default_code || '');
    setOdooId(p.id);
    setShowDropdown(false);
  };

  return (
    <tr className="adding-row">
      <td style={{position: 'relative'}}>
        <input
          type="text"
          className="inline-input"
          value={productName}
          onChange={(e) => { setProductName(e.target.value); setShowDropdown(true); }}
          placeholder="Nombre producto..."
          autoFocus
        />
        {showDropdown && filteredOdoo.length > 0 && (
          <div className="product-dropdown">
            {filteredOdoo.map(p => (
              <div key={p.id} className="dropdown-item" onClick={() => selectProduct(p)}>
                <span>{p.name}</span>
                {p.default_code && <code>{p.default_code}</code>}
              </div>
            ))}
          </div>
        )}
      </td>
      <td>
        <input type="text" className="inline-input" value={productCode} onChange={(e) => setProductCode(e.target.value)} placeholder="Código" />
      </td>
      <td>
        <input
          type="text"
          className="inline-input"
          value={eanCode}
          onChange={(e) => setEanCode(e.target.value)}
          placeholder="EAN/EDI..."
          onKeyDown={(e) => {
            if (e.key === 'Enter' && eanCode && productName) onSave({ product_name: productName, product_code: productCode, ean_code: eanCode, odoo_product_id: odooId });
            if (e.key === 'Escape') onCancel();
          }}
        />
      </td>
      <td>
        <button className="btn-icon sm" onClick={() => { if (eanCode && productName) onSave({ product_name: productName, product_code: productCode, ean_code: eanCode, odoo_product_id: odooId }); }}><Save size={14} /></button>
        <button className="btn-icon sm" onClick={onCancel}><X size={14} /></button>
      </td>
    </tr>
  );
};

// Modal for creating/editing customer groups
const GroupModal = ({ group, categories, onSave, onClose }) => {
  const [name, setName] = useState(group?.name || '');
  const [selectedCats, setSelectedCats] = useState(group?.category_ids || []);
  const [selectedNames, setSelectedNames] = useState(group?.category_names || []);

  const toggleCat = (cat) => {
    if (selectedCats.includes(cat.id)) {
      setSelectedCats(selectedCats.filter(id => id !== cat.id));
      setSelectedNames(selectedNames.filter(n => n !== cat.name));
    } else {
      setSelectedCats([...selectedCats, cat.id]);
      setSelectedNames([...selectedNames, cat.name]);
    }
  };

  return (
    <div className="modal-overlay" data-testid="group-modal">
      <div className="modal">
        <div className="modal-header">
          <h2>{group ? 'Editar Grupo' : 'Nuevo Grupo de Clientes'}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Nombre del grupo</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: SPAR, ALCAMPO..."
              autoFocus
              data-testid="group-name-input"
            />
          </div>
          {categories.length > 0 && (
            <div className="form-group">
              <label>Categorías de Odoo asociadas (opcional)</label>
              <div className="category-chips">
                {categories.map(cat => (
                  <span
                    key={cat.id}
                    className={`category-chip ${selectedCats.includes(cat.id) ? 'selected' : ''}`}
                    onClick={() => toggleCat(cat)}
                  >
                    {cat.name}
                  </span>
                ))}
              </div>
              <small className="help-text">Asocia categorías de Odoo para buscar EANs automáticamente al procesar facturas</small>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button
            className="btn btn-primary"
            onClick={() => onSave({ id: group?.id, name, category_ids: selectedCats, category_names: selectedNames })}
            disabled={!name.trim()}
            data-testid="save-group-btn"
          >
            <Save size={16} /> Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

// Invoices Page
const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [dateFrom, setDateFrom] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [selected, setSelected] = useState(new Set());
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [toast, setToast] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.get('/companies/customer-groups').then(setGroups).catch(() => setGroups([]));
  }, []);

  const loadInvoices = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (groupFilter) params.set('group_id', groupFilter);
    const qs = params.toString();
    api.get(`/invoices${qs ? `?${qs}` : ''}`)
      .then(setInvoices)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [statusFilter, groupFilter]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const fetchFromOdoo = async () => {
    setFetching(true);
    try {
      const result = await api.post(`/invoices/fetch-odoo?date_from=${dateFrom}&date_to=${dateTo}`);
      setToast({ message: result.message, type: 'success' });
      loadInvoices();
    } catch (e) {
      setToast({ message: 'Error al obtener facturas', type: 'error' });
    }
    setFetching(false);
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  };

  const selectAll = () => {
    const allIds = invoices.map(i => i.id);
    if (selected.size === allIds.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
  };

  const processSelected = async () => {
    if (selected.size === 0) {
      setToast({ message: 'Selecciona al menos una factura', type: 'error' });
      return;
    }
    
    const selectedInvoices = invoices.filter(i => selected.has(i.id));
    const alreadySent = selectedInvoices.filter(i => i.state === 'sent');
    if (alreadySent.length > 0) {
      const nums = alreadySent.map(i => i.invoice_number).join(', ');
      if (!window.confirm(`Las siguientes facturas ya fueron enviadas:\n${nums}\n\n¿Deseas reenviarlas?`)) {
        return;
      }
    }
    
    setProcessing(true);
    try {
      const result = await api.post('/invoices/process', { invoice_ids: Array.from(selected) });
      const failed = result.results?.filter(r => !r.success) || [];
      if (failed.length > 0) {
        const errors = failed.map(f => f.error || 'Error desconocido').join('\n');
        setToast({ message: `${result.results.length - failed.length} enviadas, ${failed.length} con error:\n${errors}`, type: 'error' });
      } else {
        setToast({ message: result.message, type: 'success' });
      }
      setSelected(new Set());
      loadInvoices();
    } catch (e) {
      setToast({ message: e.response?.data?.detail || 'Error al procesar facturas', type: 'error' });
    }
    setProcessing(false);
  };

  const previewEdi = async (invoiceId) => {
    try {
      const result = await api.get(`/invoices/${invoiceId}/preview`);
      setPreviewInvoice(result);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Error desconocido';
      setToast({ message: `Error vista previa: ${detail}`, type: 'error' });
    }
  };

  const downloadEdi = (invoiceId) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backendUrl}/api/invoices/${invoiceId}/download-edi?token=${localStorage.getItem('edi_token')}`, '_blank');
  };

  const downloadFromPreview = () => {
    if (!previewInvoice) return;
    const blob = new Blob([previewInvoice.edi_content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = previewInvoice.filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 200);
  };

  const downloadBatch = async () => {
    if (selected.size === 0) {
      setToast({ message: 'Selecciona al menos una factura', type: 'error' });
      return;
    }
    
    const selectedInvoices = invoices.filter(i => selected.has(i.id));
    const alreadySent = selectedInvoices.filter(i => i.state === 'sent');
    if (alreadySent.length > 0) {
      const nums = alreadySent.map(i => i.invoice_number).join(', ');
      if (!window.confirm(`Las siguientes facturas ya fueron enviadas:\n${nums}\n\n¿Deseas volver a descargar el EDI?`)) {
        return;
      }
    }
    setProcessing(true);
    try {
      const result = await api.post('/invoices/download-batch', { invoice_ids: Array.from(selected) });
      // Navigate to the download URL - browser handles it natively
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      window.location.href = `${backendUrl}/api/invoices/download-file/${result.download_id}?token=${localStorage.getItem('edi_token')}`;
      setToast({ message: `${result.file_count} fichero(s) EDI generados`, type: 'success' });
    } catch (e) {
      setToast({ message: e.response?.data?.detail || 'Error al generar ficheros', type: 'error' });
    }
    setProcessing(false);
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    const selInvs = invoices.filter(i => selected.has(i.id));
    const nums = selInvs.map(i => i.invoice_number).join(', ');
    if (!window.confirm(`¿Eliminar ${selInvs.length} factura(s)?\n${nums}\n\nPodrás volver a importarlas desde Odoo.`)) return;
    try {
      const result = await api.delete('/invoices', { data: { invoice_ids: Array.from(selected) } });
      setToast({ message: result.message, type: 'success' });
      setSelected(new Set());
      loadInvoices();
    } catch (e) {
      setToast({ message: e.response?.data?.detail || 'Error al eliminar', type: 'error' });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, class: 'pending', label: 'Pendiente' },
      downloaded: { icon: Download, class: 'downloaded', label: 'Descargada' },
      sent: { icon: CheckCircle, class: 'sent', label: 'Enviada' },
      error: { icon: XCircle, class: 'error', label: 'Error' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`status-badge ${badge.class}`}>
        <Icon size={14} />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="page" data-testid="invoices-page">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div className="page-header">
        <h1>Facturas EDI</h1>
      </div>

      <div className="filters-bar">
        <div className="date-filters">
          <div className="form-group">
            <label>Desde</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="date-from-input"
            />
          </div>
          <div className="form-group">
            <label>Hasta</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="date-to-input"
            />
          </div>
          <button className="btn btn-secondary" onClick={fetchFromOdoo} disabled={fetching} data-testid="fetch-invoices-btn">
            {fetching ? <RefreshCw size={18} className="spin" /> : <Download size={18} />}
            Importar de Odoo
          </button>
        </div>
        <div className="status-filter">
          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} data-testid="group-filter">
            <option value="">Todos los grupos</option>
            {groups.map(g => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
            <option value="">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="downloaded">Descargadas</option>
            <option value="sent">Enviadas</option>
            <option value="error">Con error</option>
          </select>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="selection-bar">
          <span>{selected.size} factura(s) seleccionada(s)</span>
          <div className="selection-actions">
            <button className="btn btn-danger" onClick={deleteSelected} data-testid="delete-invoices-btn">
              <Trash2 size={18} />
              Borrar seleccionadas
            </button>
            <button className="btn btn-success" onClick={downloadBatch} disabled={processing} data-testid="download-batch-btn">
              {processing ? <RefreshCw size={18} className="spin" /> : <Download size={18} />}
              Procesar y Descargar EDI
            </button>
            <button className="btn btn-primary" onClick={processSelected} disabled={processing} data-testid="process-invoices-btn">
              {processing ? <RefreshCw size={18} className="spin" /> : <Send size={18} />}
              Procesar y Enviar EDI
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : (
        <div className="table-container" data-testid="invoices-table">
          {invoices.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <p>No hay facturas. Importa desde Odoo para comenzar.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={selectAll}
                      checked={selected.size > 0 && selected.size === invoices.length}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th>Número</th>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>Dirección de entrega</th>
                  <th>Total</th>
                  <th>Estado</th>
                  <th>Fecha Envío / Descarga</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(invoice => (
                  <tr key={invoice.id} className={selected.has(invoice.id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(invoice.id)}
                        onChange={() => toggleSelect(invoice.id)}
                        data-testid={`select-invoice-${invoice.id}`}
                      />
                    </td>
                    <td><strong>{invoice.invoice_number}</strong></td>
                    <td>{formatDate(invoice.invoice_date)}</td>
                    <td>{invoice.partner_name}</td>
                    <td data-testid={`shipping-name-${invoice.id}`}>{invoice.partner_shipping_name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td className="amount">{invoice.amount_total?.toFixed(2)} €</td>
                    <td>
                      {getStatusBadge(invoice.state)}
                      {invoice.state === 'error' && invoice.error_message && (
                        <div className="error-detail">{invoice.error_message}</div>
                      )}
                    </td>
                    <td data-testid={`sent-at-${invoice.id}`}>
                      {invoice.state === 'downloaded'
                        ? formatDateTime(invoice.downloaded_at)
                        : formatDateTime(invoice.sent_at)}
                    </td>
                    <td>
                      <button className="btn-icon" onClick={() => previewEdi(invoice.id)} title="Vista previa EDI" data-testid={`preview-invoice-${invoice.id}`}>
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {previewInvoice && (
        <div className="modal-overlay" data-testid="preview-modal">
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>Vista Previa EDI - {previewInvoice.invoice_number}</h2>
              <button onClick={() => setPreviewInvoice(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p className="filename">Archivo: <code>{previewInvoice.filename}</code></p>
              <pre className="edi-preview">{previewInvoice.edi_content}</pre>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setPreviewInvoice(null)}>Cerrar</button>
              <button className="btn btn-primary" onClick={downloadFromPreview} data-testid="download-edi-btn">
                <Download size={16} /> Descargar archivo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Modal to pick synced Odoo products and add them to a group
const OdooProductPickerModal = ({ groupId, odooProducts, existingProducts, onAdd, onClose }) => {
  const [selected, setSelected] = useState(new Map()); // id -> {product, ean_code}
  const [search, setSearch] = useState('');

  const existingIds = new Set(existingProducts.map(p => p.odoo_product_id));

  const filtered = odooProducts.filter(p => {
    if (existingIds.has(p.id)) return false;
    if (!search) return true;
    return p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.default_code && p.default_code.toLowerCase().includes(search.toLowerCase()));
  });

  const toggleProduct = (product) => {
    const next = new Map(selected);
    if (next.has(product.id)) {
      next.delete(product.id);
    } else {
      next.set(product.id, { ...product, ean_code: product.barcode || '' });
    }
    setSelected(next);
  };

  const updateEan = (productId, ean) => {
    const next = new Map(selected);
    const item = next.get(productId);
    if (item) {
      next.set(productId, { ...item, ean_code: ean });
      setSelected(next);
    }
  };

  const handleAdd = () => {
    const products = Array.from(selected.values()).map(p => ({
      odoo_product_id: p.id,
      product_name: p.name,
      product_code: p.default_code || null,
      ean_code: p.ean_code || ''
    }));
    onAdd(groupId, products);
  };

  return (
    <div className="modal-overlay" data-testid="odoo-import-modal">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>Importar productos de Odoo</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          {odooProducts.length === 0 ? (
            <div className="empty-state" style={{padding: '30px'}}>
              <Package size={40} />
              <p>No hay productos sincronizados. Primero usa "Sync Productos" para traer los productos con etiqueta EDI desde Odoo.</p>
            </div>
          ) : (
            <>
              <div className="search-bar" style={{marginBottom: '16px'}}>
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Buscar producto..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="odoo-import-search"
                />
              </div>
              <div className="picker-info">
                {selected.size > 0 && <span className="picker-count">{selected.size} seleccionado(s)</span>}
              </div>
              <div className="picker-list">
                {filtered.length === 0 ? (
                  <p className="no-eans">No se encontraron productos{search ? ' para esa búsqueda' : ' disponibles'}</p>
                ) : (
                  filtered.map(product => {
                    const isSelected = selected.has(product.id);
                    return (
                      <div key={product.id} className={`picker-row ${isSelected ? 'selected' : ''}`}>
                        <label className="picker-check">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleProduct(product)}
                          />
                          <div className="picker-product-info">
                            <strong>{product.name}</strong>
                            {product.default_code && <code>{product.default_code}</code>}
                          </div>
                        </label>
                        {isSelected && (
                          <input
                            type="text"
                            className="picker-ean-input"
                            value={selected.get(product.id)?.ean_code || ''}
                            onChange={(e) => updateEan(product.id, e.target.value)}
                            placeholder="Código EAN/EDI..."
                          />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button
            className="btn btn-primary"
            onClick={handleAdd}
            disabled={selected.size === 0}
            data-testid="odoo-import-confirm-btn"
          >
            <Plus size={16} /> Añadir {selected.size || ''} producto(s)
          </button>
        </div>
      </div>
    </div>
  );
};

// ========== USERS PAGE ==========
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { user: currentUser } = useAuth();

  const loadUsers = useCallback(() => {
    setLoading(true);
    api.get('/users')
      .then(setUsers)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const saveUser = async (userData) => {
    try {
      if (userData.id) {
        const payload = {};
        if (userData.username) payload.username = userData.username;
        if (userData.password) payload.password = userData.password;
        if (userData.is_active !== undefined) payload.is_active = userData.is_active;
        await api.put(`/users/${userData.id}`, payload);
        setToast({ message: 'Usuario actualizado', type: 'success' });
      } else {
        await api.post('/users', { username: userData.username, password: userData.password });
        setToast({ message: 'Usuario creado', type: 'success' });
      }
      setShowModal(false);
      setEditingUser(null);
      loadUsers();
    } catch (e) {
      setToast({ message: e.response?.data?.detail || 'Error al guardar', type: 'error' });
    }
  };

  const deleteUser = async (u) => {
    if (u.id === currentUser?.id) {
      setToast({ message: 'No puedes eliminarte a ti mismo', type: 'error' });
      return;
    }
    if (!window.confirm(`¿Eliminar el usuario "${u.username}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setToast({ message: 'Usuario eliminado', type: 'success' });
      loadUsers();
    } catch (e) {
      setToast({ message: e.response?.data?.detail || 'Error al eliminar', type: 'error' });
    }
  };

  const toggleActive = async (u) => {
    if (u.id === currentUser?.id) {
      setToast({ message: 'No puedes desactivarte a ti mismo', type: 'error' });
      return;
    }
    try {
      await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      setToast({ message: `Usuario ${!u.is_active ? 'activado' : 'desactivado'}`, type: 'success' });
      loadUsers();
    } catch (e) {
      setToast({ message: 'Error al actualizar', type: 'error' });
    }
  };

  return (
    <div className="page" data-testid="users-page">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div className="page-header">
        <h1>Usuarios</h1>
        <button className="btn btn-primary" onClick={() => { setEditingUser(null); setShowModal(true); }} data-testid="new-user-btn">
          <Plus size={18} /> Nuevo Usuario
        </button>
      </div>

      {loading ? <div className="loading">Cargando...</div> : (
        <div className="table-container" data-testid="users-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Estado</th>
                <th>Creado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td><strong>{u.username}</strong> {u.id === currentUser?.id && <span className="badge-current">(tú)</span>}</td>
                  <td>
                    <span className={`status-badge ${u.is_active ? 'sent' : 'error'}`} onClick={() => toggleActive(u)} style={{cursor: 'pointer'}}>
                      {u.is_active ? <><CheckCircle size={14} /> Activo</> : <><XCircle size={14} /> Inactivo</>}
                    </span>
                  </td>
                  <td>{u.created_at ? formatDate(u.created_at) : '-'}</td>
                  <td>
                    <button className="btn-icon" onClick={() => { setEditingUser(u); setShowModal(true); }} title="Editar" data-testid={`edit-user-${u.id}`}>
                      <Edit size={16} />
                    </button>
                    <button className="btn-icon danger" onClick={() => deleteUser(u)} title="Eliminar" data-testid={`delete-user-${u.id}`} disabled={u.id === currentUser?.id}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <UserModal
          user={editingUser}
          onSave={saveUser}
          onClose={() => { setShowModal(false); setEditingUser(null); }}
        />
      )}
    </div>
  );
};

const UserModal = ({ user, onSave, onClose }) => {
  const [username, setUsername] = useState(user?.username || '');
  const [password, setPassword] = useState('');

  return (
    <div className="modal-overlay" data-testid="user-modal">
      <div className="modal">
        <div className="modal-header">
          <h2>{user ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Nombre de usuario</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Nombre de usuario"
              autoFocus
              data-testid="user-username-input"
            />
          </div>
          <div className="form-group">
            <label>{user ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={user ? 'Sin cambios' : 'Contraseña'}
              data-testid="user-password-input"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button
            className="btn btn-primary"
            onClick={() => onSave({ id: user?.id, username, password: password || undefined })}
            disabled={!username.trim() || (!user && !password.trim())}
            data-testid="save-user-btn"
          >
            <Save size={16} /> Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

// Main App
// ========== UTILITIES PAGE ==========
const UtilitiesPage = () => {
  const [nif, setNif] = useState('');
  const [excelFile, setExcelFile] = useState(null);
  const [odooCompany, setOdooCompany] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState({});
  const [loading, setLoading] = useState('');
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState(null);

  // Activity Log state
  const [logData, setLogData] = useState({ logs: [], total: 0 });
  const [logFilter, setLogFilter] = useState({ date_from: '', date_to: '', username: '' });
  const [logUsers, setLogUsers] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logPage, setLogPage] = useState(0);
  const LOG_PAGE_SIZE = 25;

  const loadLog = useCallback(async (page = 0) => {
    setLogLoading(true);
    try {
      const params = new URLSearchParams();
      if (logFilter.date_from) params.append('date_from', logFilter.date_from);
      if (logFilter.date_to) params.append('date_to', logFilter.date_to);
      if (logFilter.username) params.append('username', logFilter.username);
      params.append('limit', LOG_PAGE_SIZE);
      params.append('offset', page * LOG_PAGE_SIZE);
      const data = await api.get(`/activity-log?${params.toString()}`);
      setLogData(data);
      setLogPage(page);
    } catch (e) {
      console.error(e);
    }
    setLogLoading(false);
  }, [logFilter]);

  const loadLogUsers = useCallback(async () => {
    try {
      const users = await api.get('/activity-log/users');
      setLogUsers(users);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadLog(0); loadLogUsers(); }, [loadLog, loadLogUsers]);

  const searchCompany = async () => {
    if (!nif.trim()) return;
    setLoading('search');
    setOdooCompany(null);
    setPreviewData(null);
    setResult(null);
    try {
      const data = await api.post('/odoo/search-company-by-nif', { nif: nif.trim() });
      setOdooCompany(data);
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Empresa no encontrada en Odoo', type: 'error' });
    } finally {
      setLoading('');
    }
  };

  const previewContacts = async () => {
    if (!excelFile || !nif.trim()) return;
    setLoading('preview');
    setPreviewData(null);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('nif', nif.trim());
      const res = await axios.post(`${API}/odoo/preview-delivery-contacts`, formData, {
        headers: { ...api._getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setPreviewData(res.data);
      const sel = {};
      res.data.contacts.forEach((_, i) => { sel[i] = true; });
      setSelectedContacts(sel);
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Error al procesar el Excel', type: 'error' });
    } finally {
      setLoading('');
    }
  };

  const createContacts = async () => {
    if (!odooCompany || !previewData?.contacts?.length) return;
    const filteredContacts = previewData.contacts.filter((_, i) => selectedContacts[i]);
    if (!filteredContacts.length) return;
    setLoading('create');
    try {
      const data = await api.post('/odoo/create-delivery-contacts', {
        parent_id: odooCompany.id,
        contacts: filteredContacts,
        company_info: odooCompany
      });
      setResult(data);
      setToast({
        message: `${data.created.length} contactos creados en Odoo y ${data.saved_in_app} guardados con P.O. en la app${data.errors.length ? `, ${data.errors.length} errores` : ''}`,
        type: data.errors.length ? 'warning' : 'success'
      });
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Error al crear contactos', type: 'error' });
    } finally {
      setLoading('');
    }
  };

  return (
    <div className="page" data-testid="utilities-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="page-header">
        <h1>Utilidades</h1>
      </div>

      {/* 1. Backup & Restore de Base de Datos */}
      <div className="config-section full-width">
        <div className="section-header">
          <Database size={20} />
          <h2>Backup y Restauracion de Base de Datos</h2>
        </div>
        <p className="section-desc">
          Descarga una copia completa de la base de datos (SQL) o restaura desde un fichero anterior.
        </p>
        <div className="backup-actions">
          <button className="btn btn-success" onClick={() => {
            const token = localStorage.getItem('edi_token');
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const a = document.createElement('a');
            a.href = `${backendUrl}/api/backup/db?token=${token}`;
            a.click();
          }} data-testid="backup-db-btn">
            <Download size={18} /> Descargar Backup SQL
          </button>
          <label className="btn btn-secondary" data-testid="restore-db-btn">
            <Upload size={18} /> Restaurar Backup SQL
            <input type="file" accept=".sql" onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              if (!window.confirm('¿Restaurar backup de base de datos? Esto reemplazara TODOS los datos actuales.')) {
                e.target.value = '';
                return;
              }
              try {
                const res = await api.upload('/backup/restore-db', file);
                setToast({ message: res.message || 'Backup restaurado correctamente', type: 'success' });
              } catch (err) {
                setToast({ message: err.response?.data?.detail || 'Error al restaurar', type: 'error' });
              }
              e.target.value = '';
            }} hidden />
          </label>
        </div>
      </div>

      {/* 2. Crear Contactos de Entrega */}
      <div className="config-section full-width" style={{marginTop: '24px'}}>
        <div className="section-header">
          <Building2 size={20} />
          <h2>Crear Contactos de Entrega en Odoo</h2>
        </div>
        <p className="section-desc">
          Crea automáticamente los contactos de entrega en Odoo para una compañía, usando el fichero Excel de P.O.
        </p>

        <div className="form-grid" style={{marginTop: '16px'}}>
          <div className="form-group">
            <label>1. NIF de la compañía</label>
            <div style={{display: 'flex', gap: '8px'}}>
              <input
                type="text"
                value={nif}
                onChange={(e) => setNif(e.target.value)}
                placeholder="Ej: B35105055"
                data-testid="nif-input"
                onKeyDown={(e) => e.key === 'Enter' && searchCompany()}
              />
              <button
                className="btn btn-secondary"
                onClick={searchCompany}
                disabled={loading === 'search' || !nif.trim()}
                data-testid="search-company-btn"
              >
                {loading === 'search' ? <RefreshCw size={16} className="spin" /> : <Search size={16} />}
                Buscar
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>2. Fichero Excel de P.O.</label>
            <input
              type="file"
              accept=".xls,.xlsx"
              onChange={(e) => { setExcelFile(e.target.files[0]); setPreviewData(null); setResult(null); }}
              data-testid="excel-file-input"
            />
          </div>
        </div>

        {odooCompany && (
          <div className="info-box" style={{marginTop: '16px', padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)'}}>
            <p><strong>Empresa encontrada en Odoo:</strong> {odooCompany.name}</p>
            <p style={{fontSize: '0.9em', color: 'var(--text-secondary)'}}>
              NIF: {odooCompany.vat} | {odooCompany.street}, {odooCompany.city} {odooCompany.zip}
              {odooCompany.existing_delivery_contacts > 0 &&
                <span style={{marginLeft: '12px', color: 'var(--warning-color)'}}>
                  ({odooCompany.existing_delivery_contacts} contactos de entrega ya existentes)
                </span>
              }
            </p>
            <button
              className="btn btn-primary"
              style={{marginTop: '8px'}}
              onClick={previewContacts}
              disabled={loading === 'preview' || !excelFile}
              data-testid="preview-contacts-btn"
            >
              {loading === 'preview' ? <RefreshCw size={16} className="spin" /> : <Eye size={16} />}
              Ver contactos a crear desde Excel
            </button>
          </div>
        )}

        {previewData && (
          <div style={{marginTop: '16px'}}>
            <h3 style={{marginBottom: '8px'}}>
              Contactos a crear bajo "{odooCompany?.name}" ({Object.values(selectedContacts).filter(Boolean).length} de {previewData.contacts_count} seleccionados)
            </h3>
            <div className="table-container" style={{maxHeight: '400px', overflow: 'auto'}}>
              <table className="data-table" data-testid="preview-contacts-table">
                <thead>
                  <tr>
                    <th style={{width: '40px'}}>
                      <input
                        type="checkbox"
                        checked={Object.values(selectedContacts).every(Boolean)}
                        onChange={(e) => {
                          const sel = {};
                          previewData.contacts.forEach((_, i) => { sel[i] = e.target.checked; });
                          setSelectedContacts(sel);
                        }}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th>#</th>
                    <th>Nombre</th>
                    <th>Dirección</th>
                    <th>CP</th>
                    <th>Población</th>
                    <th>BY</th>
                    <th>DP</th>
                    <th>IV</th>
                    <th>PR</th>
                    <th>Teléfono</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.contacts.map((c, i) => (
                    <tr key={i} style={{opacity: selectedContacts[i] ? 1 : 0.4}}>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!selectedContacts[i]}
                          onChange={(e) => setSelectedContacts(prev => ({...prev, [i]: e.target.checked}))}
                          data-testid={`select-contact-${i}`}
                        />
                      </td>
                      <td>{i + 1}</td>
                      <td>{c.name}</td>
                      <td>{c.street}</td>
                      <td>{c.zip}</td>
                      <td>{c.city}</td>
                      <td style={{fontSize: '0.8em'}}>{c.po_by}</td>
                      <td style={{fontSize: '0.8em'}}>{c.po_dp}</td>
                      <td style={{fontSize: '0.8em'}}>{c.po_iv}</td>
                      <td style={{fontSize: '0.8em'}}>{c.po_pr}</td>
                      <td style={{fontSize: '0.8em'}}>{c.phone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!result && (
              <button
                className="btn btn-primary"
                style={{marginTop: '12px'}}
                onClick={createContacts}
                disabled={loading === 'create' || !Object.values(selectedContacts).some(Boolean)}
                data-testid="create-contacts-btn"
              >
                {loading === 'create' ? <RefreshCw size={16} className="spin" /> : <Upload size={16} />}
                Crear {Object.values(selectedContacts).filter(Boolean).length} contactos en Odoo
              </button>
            )}

            {result && (
              <div style={{marginTop: '12px', padding: '12px 16px', borderRadius: '8px',
                background: result.errors.length ? '#fff3cd' : '#d1e7dd',
                border: `1px solid ${result.errors.length ? '#ffc107' : '#198754'}`}}>
                <p><CheckCircle size={16} style={{verticalAlign: 'middle', marginRight: '6px'}} />
                  <strong>{result.created.length}</strong> contactos creados correctamente.
                </p>
                {result.errors.length > 0 && (
                  <div style={{marginTop: '8px'}}>
                    <p><XCircle size={16} style={{verticalAlign: 'middle', marginRight: '6px'}} />
                      <strong>{result.errors.length}</strong> errores:</p>
                    <ul style={{marginTop: '4px', paddingLeft: '20px'}}>
                      {result.errors.map((e, i) => (
                        <li key={i}>{e.name}: {e.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 3. Log de Actividad */}
      <div className="config-section full-width" style={{marginTop: '24px'}}>
        <div className="section-header">
          <FileText size={20} />
          <h2>Log de Actividad</h2>
        </div>
        <div className="log-filters" data-testid="log-filters">
          <div className="form-group">
            <label>Desde</label>
            <input type="date" value={logFilter.date_from} onChange={(e) => setLogFilter({...logFilter, date_from: e.target.value})} />
          </div>
          <div className="form-group">
            <label>Hasta</label>
            <input type="date" value={logFilter.date_to} onChange={(e) => setLogFilter({...logFilter, date_to: e.target.value})} />
          </div>
          <div className="form-group">
            <label>Usuario</label>
            <select value={logFilter.username} onChange={(e) => setLogFilter({...logFilter, username: e.target.value})}>
              <option value="">Todos</option>
              {logUsers.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <button className="btn btn-secondary" onClick={() => loadLog(0)} style={{alignSelf: 'flex-end'}}>
            <Search size={16} /> Buscar
          </button>
          <button className="btn btn-secondary" onClick={() => { setLogFilter({ date_from: '', date_to: '', username: '' }); }} style={{alignSelf: 'flex-end'}}>
            <X size={16} /> Limpiar
          </button>
        </div>

        {logLoading ? <div className="loading">Cargando...</div> : (
          <>
            <div className="table-container" data-testid="activity-log-table">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{width: '160px'}}>Fecha y Hora</th>
                    <th style={{width: '100px'}}>Usuario</th>
                    <th>Accion</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {logData.logs.length === 0 ? (
                    <tr><td colSpan="4" style={{textAlign: 'center', color: 'var(--text-muted)'}}>Sin registros</td></tr>
                  ) : logData.logs.map(log => (
                    <tr key={log.id}>
                      <td style={{whiteSpace: 'nowrap', fontSize: '0.85rem'}}>{formatDateTime(log.created_at)}</td>
                      <td><strong>{log.username}</strong></td>
                      <td>{log.action}</td>
                      <td style={{color: 'var(--text-secondary)', fontSize: '0.85rem'}}>{log.detail || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {logData.total > LOG_PAGE_SIZE && (
              <div className="log-pagination">
                <button className="btn btn-secondary btn-sm" disabled={logPage === 0} onClick={() => loadLog(logPage - 1)}>Anterior</button>
                <span>{logPage * LOG_PAGE_SIZE + 1}-{Math.min((logPage + 1) * LOG_PAGE_SIZE, logData.total)} de {logData.total}</span>
                <button className="btn btn-secondary btn-sm" disabled={(logPage + 1) * LOG_PAGE_SIZE >= logData.total} onClick={() => loadLog(logPage + 1)}>Siguiente</button>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="app-loading"><RefreshCw size={32} className="spin" /><span>Cargando...</span></div>;
  }

  if (!user) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<LoginPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <div className="app-container">
      <BrowserRouter>
        <Sidebar />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/config" element={<Configuration />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/utilities" element={<UtilitiesPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </BrowserRouter>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
